import React, { useState, useEffect, useMemo } from 'react'
import { format, eachDayOfInterval, getDay, isToday } from 'date-fns'
import { pt } from 'date-fns/locale'
import { useNavigate } from 'react-router-dom'
import { getAgendamentosMes } from '../../lib/supabase'
import { useAuth } from '../../contexts/AuthContext'
import { useTenant } from '../../contexts/TenantContext'
import { ESTADOS, ORDEM_ESTADOS } from '../../lib/estados'
import Loading from '../../components/Loading'

const DIAS_SEMANA = ['Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb', 'Dom']

export default function AgendaMes() {
  const navigate = useNavigate()
  const { tenant } = useTenant()
  const { perfil, podeFazer, estadosVisiveisAgenda } = useAuth()
  const meuProfId = perfil?.profissional_id
  // Sem permissão para ver a agenda de todos → trancado à própria.
  const trancadoAMinha = !podeFazer('ver_agenda_todos') && !!meuProfId
  const hoje = new Date()
  const [ano, setAno] = useState(hoje.getFullYear())
  const [mes, setMes] = useState(hoje.getMonth() + 1)
  const [agendamentos, setAgendamentos] = useState([])
  // Por defeito mostra só as próprias apenas quem está trancado à própria agenda;
  // quem pode ver todos (ex.: manager) abre a ver todos.
  const [soMinhas, setSoMinhas] = useState(trancadoAMinha)
  const [carregando, setCarregando] = useState(true)

  useEffect(() => {
    if (!tenant) return
    setCarregando(true)
    getAgendamentosMes(tenant.id, ano, mes)
      .then(setAgendamentos)
      .catch(console.error)
      .finally(() => setCarregando(false))
  }, [ano, mes, tenant])

  function mudarMes(delta) {
    let m = mes + delta
    let a = ano
    if (m > 12) { m = 1; a++ }
    if (m < 1)  { m = 12; a-- }
    setMes(m)
    setAno(a)
  }

  const agendamentosVisiveis = useMemo(
    () => {
      let lista = (meuProfId && (soMinhas || trancadoAMinha))
        ? agendamentos.filter(a => a.profissional_id === meuProfId)
        : agendamentos
      // Só os estados que este perfil pode ver.
      return lista.filter(a => estadosVisiveisAgenda.has(a.estado))
    },
    [agendamentos, meuProfId, soMinhas, trancadoAMinha, estadosVisiveisAgenda]
  )

  // Contagem por dia e por estado: porDia[dia][estado] = nº de marcações.
  const porDia = {}
  agendamentosVisiveis.forEach(ag => {
    const k = format(new Date(ag.data_hora_inicio), 'yyyy-MM-dd')
    if (!porDia[k]) porDia[k] = {}
    porDia[k][ag.estado] = (porDia[k][ag.estado] || 0) + 1
  })

  const estadosLegenda = ORDEM_ESTADOS.filter(e => estadosVisiveisAgenda.has(e))

  const primeiroDia = new Date(ano, mes - 1, 1)
  const ultimoDia = new Date(ano, mes, 0)
  const dias = eachDayOfInterval({ start: primeiroDia, end: ultimoDia })
  const inicioSemana = (getDay(primeiroDia) + 6) % 7

  const mesLabel = format(primeiroDia, "MMMM 'de' yyyy", { locale: pt })

  return (
    <div style={{ maxWidth: '820px', margin: '0 auto', padding: '32px 24px 64px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '28px' }}>
        <h2 style={{ fontSize: '22px', textTransform: 'capitalize' }}>{mesLabel}</h2>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button onClick={() => mudarMes(-1)} className="btn-secundario" style={{ padding: '8px 14px' }}>←</button>
          <button
            onClick={() => { setAno(hoje.getFullYear()); setMes(hoje.getMonth() + 1) }}
            className="btn-secundario"
            style={{ padding: '8px 14px', fontSize: '12px' }}
          >
            Hoje
          </button>
          <button onClick={() => mudarMes(1)} className="btn-secundario" style={{ padding: '8px 14px' }}>→</button>
        </div>
      </div>

      {meuProfId && !trancadoAMinha && (
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', marginBottom: '20px', fontSize: '13px', cursor: 'pointer', color: 'var(--cor-texto-suave)' }}>
          <input
            type="checkbox"
            checked={soMinhas}
            onChange={e => setSoMinhas(e.target.checked)}
            style={{ width: '16px', height: '16px', accentColor: 'var(--cor-principal)' }}
          />
          <span>Apenas as minhas marcações</span>
        </label>
      )}

      {!carregando && estadosLegenda.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '14px', marginBottom: '18px' }}>
          {estadosLegenda.map(e => (
            <span key={e} style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '13px' }}>
              <span style={{ width: '10px', height: '10px', borderRadius: '50%', background: ESTADOS[e].cor, flexShrink: 0 }} />
              <span style={{ color: 'var(--cor-texto)' }}>{ESTADOS[e].label}</span>
            </span>
          ))}
        </div>
      )}

      {carregando ? <Loading /> : (
        <div style={{
          background: 'var(--cor-branco)', border: '1px solid var(--cor-borda)',
          borderRadius: 'var(--raio-lg)', overflow: 'hidden', boxShadow: 'var(--sombra)',
        }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', background: 'var(--cor-creme)', borderBottom: '1px solid var(--cor-borda)' }}>
            {DIAS_SEMANA.map(d => (
              <div key={d} style={{ padding: '10px 0', textAlign: 'center', fontSize: '12px', fontWeight: '600', color: 'var(--cor-texto-suave)' }}>
                {d}
              </div>
            ))}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)' }}>
            {Array.from({ length: inicioSemana }).map((_, i) => (
              <div key={`e${i}`} style={{
                minHeight: '80px',
                background: 'var(--cor-creme)',
                borderRight: '1px solid var(--cor-borda)',
                borderBottom: '1px solid var(--cor-borda)',
              }} />
            ))}

            {dias.map((dia, i) => {
              const key = format(dia, 'yyyy-MM-dd')
              const contagens = porDia[key] || {}
              const estadosDia = estadosLegenda.filter(e => contagens[e] > 0)
              const total = estadosDia.reduce((s, e) => s + contagens[e], 0)
              const eHoje = isToday(dia)
              const col = (inicioSemana + i) % 7

              return (
                <div
                  key={key}
                  onClick={() => navigate(`/admin?data=${key}`)}
                  title={total > 0 ? `${total} marcação${total !== 1 ? 'ões' : ''}` : 'Ver agenda'}
                  style={{
                    minHeight: '80px',
                    padding: '8px',
                    borderRight: col === 6 ? 'none' : '1px solid var(--cor-borda)',
                    borderBottom: '1px solid var(--cor-borda)',
                    cursor: 'pointer',
                    background: eHoje ? '#FFF7F3' : 'var(--cor-branco)',
                    transition: 'background 0.12s',
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = 'var(--cor-creme)'}
                  onMouseLeave={e => e.currentTarget.style.background = eHoje ? '#FFF7F3' : 'var(--cor-branco)'}
                >
                  <div style={{
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                    width: '26px', height: '26px', borderRadius: '50%',
                    background: eHoje ? 'var(--cor-principal)' : 'transparent',
                    color: eHoje ? 'white' : 'var(--cor-texto)',
                    fontSize: '13px', fontWeight: eHoje ? '600' : '400',
                    marginBottom: '6px',
                  }}>
                    {format(dia, 'd')}
                  </div>
                  {estadosDia.length > 0 && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                      {estadosDia.map(e => (
                        <span key={e} title={`${contagens[e]} ${ESTADOS[e].label}`} style={{
                          fontSize: '11px',
                          background: ESTADOS[e].cor,
                          color: 'white',
                          padding: '2px 7px',
                          borderRadius: '10px',
                          fontWeight: '500',
                        }}>
                          {contagens[e]}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        </div>
      )}

      <p style={{ marginTop: '14px', fontSize: '12px', color: 'var(--cor-texto-suave)' }}>
        Clique num dia para ver os detalhes da agenda.
      </p>
    </div>
  )
}